// libraries
import React, { useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { SwipeableDrawer, Stack, Divider, Button, Box } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material'
import { useForm, FormProvider } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { formOptions } from 'utils/validation/freightCost'
import { startCase, toLower } from 'lodash'

// queries
import { useGetCustomerQuery } from 'services/customers'

// specific components
import FreightCostBladeCarrierInformation from 'components/containers/freight/freight-cost/FreightCostBladeCarrierInformation'
import FreightCostBladeFreightCost from 'components/containers/freight/freight-cost/FreightCostBladeFreightCost'

// generic components
import FormModalHeading from 'components/generic/modals/form-modal/FormModalHeading'
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'
import Loading from 'components/generic/Loading'
import { Chip } from 'components/generic/chip'

// helpers and utils
import { useCalculateFreightCost } from 'utils/custom-hooks/useCalculateFreightCost'
import { TRANSPORTATION_TYPE_OPTIONS, BILL_STATUSES, TICKET_STATUSES, INVOICE_STATUSES } from 'utils/constants'
import useGetPricePrepend from 'utils/custom-hooks/useGetPricePrepend'

const FreightCostBlade = ({
  open,
  setOpen,
  getValues,
  setValue,
  ticketStatus,
  netWeight,
  location,
  type,
  pricePrepend,
  freightBillData,
  isInLockedAccountingPeriodV1,
  contract,
  disableChargeback,
}) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const { freightBillStatus, freightBillIsPaidOrVoid, freightBillIsVoid } = useMemo(
    () => ({
      freightBillStatus: freightBillData?.status,
      freightBillIsPaidOrVoid:
        [BILL_STATUSES.PAID.id, BILL_STATUSES.PARTIAL.id, BILL_STATUSES.VOID.id].indexOf(freightBillData?.status) > -1,
      freightBillIsVoid: [BILL_STATUSES.VOID.id].indexOf(freightBillData?.status) > -1,
    }),
    [freightBillData],
  )

  const { ticketIsPaidOrVoid, ticketIsVoid } = useMemo(() => {
    return {
      ticketIsPaidOrVoid:
        [TICKET_STATUSES.PAID.id, TICKET_STATUSES.VOID.id, TICKET_STATUSES.VOIDED.id].indexOf(ticketStatus) > -1,
      ticketIsVoid: [TICKET_STATUSES.VOID.id, TICKET_STATUSES.VOIDED.id].indexOf(ticketStatus) > -1,
    }
  }, [ticketStatus])

  const methods = useForm(formOptions)
  const { handleSubmit, formState, reset, watch } = methods
  const { dirtyFields } = formState
  const isDirty = !!Object.keys(dirtyFields).length
  const selectedCarrier = watch('carrier')
  const transportationType = watch('transportationType')
  const effectiveDate = (
    getValues('effectiveDate') ||
    getValues('customer.effectiveDate') ||
    getValues('consumerInfo.effectiveDate')
  )?.toISOString()

  const { isLoading, priorityFreightCostInfo } = useCalculateFreightCost({
    location,
    transportationType,
    carrierId: selectedCarrier?.id || '',
    effectiveDate,
    netWeight,
    ticketType: type,
    contract: contract,
  })

  const { data: carrierData, isLoading: isLoadingCarrierData } = useGetCustomerQuery(
    { customerId: selectedCarrier?.id },
    { skip: !selectedCarrier?.id },
  )

  const { pricePrepend: carrierPricePrepend } = useGetPricePrepend({
    customerData: carrierData,
    selectedCustomer: selectedCarrier,
  })

  const [showCloseModal, setShowCloseModal] = useState(false)

  const onSubmit = async data => {
    if (!freightBillIsPaidOrVoid && !ticketIsVoid) {
      setValue('transportationInfo.carrierLookup', data.carrier, { shouldDirty: true })
      setValue('transportationInfo.transportationType', data.transportationType, { shouldDirty: true })
      setValue('freightCost.userSet', !data.autocalculateFreightCost, { shouldDirty: true })
      setValue('freightCost.userChargebackSet', !data.autoCalculateFreightChargeback, { shouldDirty: true })
      setValue('freightCost.cost', Number(data.cost || 0), { shouldDirty: true })
    }
    if (!ticketIsPaidOrVoid) {
      setValue(
        'freightCost.chargeBack',
        Number(data.negativeChargeback ? -Math.abs(data.chargebackAmount) : Math.abs(data.chargebackAmount) || 0),
        { shouldDirty: true },
      )
    }
    reset(data)
    setOpen(false)
  }

  useEffect(() => {
    if (open) {
      const currentTicketData = getValues()
      reset({
        carrier:
          freightBillIsPaidOrVoid || ticketIsVoid || isInLockedAccountingPeriodV1
            ? currentTicketData.transportationInfo?.carrierLookup?.displayName || ''
            : currentTicketData.transportationInfo?.carrierLookup || null,
        transportationType:
          freightBillIsPaidOrVoid || ticketIsVoid || isInLockedAccountingPeriodV1
            ? TRANSPORTATION_TYPE_OPTIONS.find(
                option => option.id === currentTicketData.transportationInfo?.transportationType,
              )?.name || ''
            : currentTicketData.transportationInfo?.transportationType || '',
        cost: currentTicketData.freightCost?.cost || 0,
        autocalculateFreightCost: !currentTicketData.freightCost?.userSet,
        autoCalculateFreightChargeback: !currentTicketData.freightCost?.userChargebackSet,
        chargebackAmount: Math.abs(currentTicketData.freightCost?.chargeBack) || 0,
        chargeback: (currentTicketData.freightCost?.chargeBack || 0) !== 0,
        negativeChargeback: (currentTicketData.freightCost?.chargeBack || 0) < 0,
      })
    }
  }, [open, freightBillIsPaidOrVoid, ticketIsVoid, ticketIsPaidOrVoid, isInLockedAccountingPeriodV1])

  return (
    <SwipeableDrawer
      anchor="right"
      open={open}
      onClose={() => (isDirty ? setShowCloseModal(true) : setOpen(false))}
      onOpen={() => setOpen(true)}
      PaperProps={{
        sx: {
          maxWidth: '1024px',
          width: '100%',
        },
      }}
      disableSwipeToOpen
    >
      <AlertModal
        open={showCloseModal}
        modalType="close"
        title="Leave This Page"
        question="Are you sure you want to leave this page?"
        description="You have unsaved data on this page and all unsaved data on the ticket will be lost."
        confirmLabel="YES, LEAVE PAGE"
        cancelLabel="GO BACK"
        onClose={() => {
          setShowCloseModal(false)
        }}
        onSubmit={() => {
          setShowCloseModal(false)
          setOpen(false)
        }}
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        {(isLoading || isLoadingCarrierData) && <Loading />}
        <Stack height="100vh">
          <FormModalHeading
            title="Freight Cost"
            backButtonPosition="right"
            onBack={() => (isDirty ? setShowCloseModal(true) : setOpen(false))}
            statusChip={
              freightBillStatus ? (
                <Chip
                  icon={freightBillStatus === BILL_STATUSES.PAID.id ? <CheckCircleIcon /> : null}
                  label={BILL_STATUSES[freightBillStatus]?.name || startCase(toLower(freightBillStatus || ''))}
                  sx={{
                    '.MuiChip-icon': {
                      color: theme.palette.primary.main,
                    },
                  }}
                />
              ) : null
            }
          />
          <Divider />
          <Box px={4} py={2} flexGrow={1}>
            <FormProvider {...methods}>
              <FreightCostBladeCarrierInformation
                disableFields={freightBillIsPaidOrVoid || ticketIsVoid || isInLockedAccountingPeriodV1}
              />
              <FreightCostBladeFreightCost
                disableFreightCost={freightBillIsPaidOrVoid || ticketIsVoid || isInLockedAccountingPeriodV1}
                disableChargeback={disableChargeback ?? (ticketIsPaidOrVoid || isInLockedAccountingPeriodV1)}
                priorityFreightCostInfo={priorityFreightCostInfo}
                type={type}
                pricePrepend={pricePrepend}
                carrierPricePrepend={carrierPricePrepend}
              />
            </FormProvider>
          </Box>
          <Divider />
          <Stack direction="row" p={2} spacing={2}>
            {isDirty && !ticketIsVoid && !freightBillIsVoid ? (
              <Button variant="contained" onClick={handleSubmit(onSubmit)}>
                Save
              </Button>
            ) : null}
            {!isDirty && freightBillData?.id ? (
              <Button variant="contained" onClick={() => navigate(`/app/freight/freight-bills/${freightBillData?.id}`)}>
                View Freight Bill
              </Button>
            ) : null}
            <Button variant="outlined" onClick={() => (isDirty ? setShowCloseModal(true) : setOpen(false))}>
              {isDirty ? 'Cancel' : 'Close'}
            </Button>
          </Stack>
        </Stack>
      </form>
    </SwipeableDrawer>
  )
}

FreightCostBlade.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  getValues: PropTypes.func.isRequired,
  setValue: PropTypes.func,
  ticketStatus: PropTypes.string,
  netWeight: PropTypes.number,
  location: PropTypes.object,
  type: PropTypes.oneOf(['IBT', 'OBT']).isRequired,
  pricePrepend: PropTypes.string,
  freightBillData: PropTypes.object,
  isInLockedAccountingPeriodV1: PropTypes.bool,
  disableChargeback: PropTypes.bool,
}

FreightCostBlade.defaultProps = {
  setValue: () => {},
  ticketStatus: TICKET_STATUSES.INCOMPLETE.id,
  netWeight: 0,
  location: null,
  pricePrepend: '$',
  freightBillData: null,
  isInLockedAccountingPeriodV1: false,
  disableChargeback: undefined,
}

export default FreightCostBlade
